import { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { MessageMedium } from 'modules/graphql/types';
import { ContentSwap } from 'ui-beta/contentSwap';
import { Button, BUTTON_SIZES } from 'ui/button';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { CircleSpinner } from 'ui/spinner';
import { ModalFooter, ModalHeader, MODAL_SIZES, MODAL_VARIANTS } from 'ui/modal';
import { BasicFilterSelect } from 'ui/selects';
import { isValidEmail } from 'utils/stringHelpers';
import { selectUserProjectIsUuidBased } from 'core/userContext';
import { sendProofFilter, getUuidPromise, displayContent } from 'modules/templateEditor/common';
import {
  TemplateDetailsContainer,
  TemplateDetailsBlock,
} from 'modules/templateEditor/common/EditableHeader/EditableHeader.styled';
import {
  useSendTestMsgContext,
  TestMsgModals,
} from 'modules/templateEditor/context/SendTestMsgContext';
import { useTemplateQueryContext } from 'modules/templateEditor/context/TemplateQueryContext';
import { addNegativeToast } from 'modules/toastContainer';
import { UserDataSummaryTable } from './UserDataSummaryTable';
import {
  FieldRow,
  ScrollableModalBody,
  StyledModal,
  StyledModalBody,
  StyledModalSubheader,
} from '../SendTestMsgModals.styled';

enum MODAL_VIEWS {
  SEND_PROOF = 'send-proof',
  USER_DATA_SUMMARY = 'user-data-summary',
}

export const SendTestMsgAsRandomModal = () => {
  const {
    emailListOptions,
    isFetchingLists,
    isSendingTestMsg,
    onSendTestAsRandom,
    setOpenTestMsgModal,
    sendErrors,
  } = useSendTestMsgContext();
  const { onSendTestAsRandom: sendTestError } = sendErrors;
  const { medium } = useTemplateQueryContext();
  const [recipient, setRecipient] = useState<string>('');
  const [testUserSize, setTestUserSize] = useState<string>('1');
  const [selectedListId, setSelectedListId] = useState<number>();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const dispatch = useDispatch();
  const [activeView, setActiveView] = useState<MODAL_VIEWS>(MODAL_VIEWS.SEND_PROOF);

  const canSend = isValidEmail(recipient) && selectedListId && parseInt(testUserSize, 10) > 0;

  const memoizedSelectedOptions = useMemo(() => {
    return sendProofFilter(searchTerm, [...emailListOptions]);
  }, [emailListOptions, searchTerm]);

  // UUID stuff
  const isUuidBased = useSelector(selectUserProjectIsUuidBased);
  const placeholderRecipient = isUuidBased
    ? displayContent.placeholderUserEmailOrId
    : displayContent.placeholderRecipient;
  const disableIfTrue = !selectedListId || recipient.length === 0;
  const disableButton = isUuidBased ? disableIfTrue : !canSend;

  const handleCloseModal = () => {
    setOpenTestMsgModal(TestMsgModals.None);
  };

  const handleSendTestMsg = async () => {
    let sendTo = recipient;
    if (isUuidBased && !isValidEmail(recipient)) {
      await Promise.resolve(getUuidPromise(recipient))
        .then((userEmail) => {
          sendTo = userEmail;
        })
        .catch(() => {
          dispatch(addNegativeToast(`User does not exist. UserId: ${recipient}`));
        });
    }
    if (selectedListId) {
      onSendTestAsRandom(selectedListId, sendTo, parseInt(testUserSize, 10), recipient);
    }
    // Timeout avoids console warning "Form submission canceled because the form is not connected"
    setTimeout(() => {
      if (!sendTestError) {
        setActiveView(MODAL_VIEWS.USER_DATA_SUMMARY);
      }
    }, 100);
  };

  return (
    <StyledModal
      id="sendTestMsgAsRandomModal"
      isModalOpen={true}
      onClose={handleCloseModal}
      size={MODAL_SIZES.MEDIUM}
      variant={MODAL_VARIANTS.DEFAULT}
    >
      <ModalHeader autoFocus={false}>
        <ContentSwap activeView={activeView}>
          <div id={MODAL_VIEWS.SEND_PROOF}>
            {displayContent.strSendTestAsRandomTitle({ isEmail: medium === MessageMedium.Email })}
          </div>
          <div id={MODAL_VIEWS.USER_DATA_SUMMARY}>{displayContent.strUserDataSummary}</div>
        </ContentSwap>
      </ModalHeader>
      {isFetchingLists ? (
        <CircleSpinner />
      ) : (
        <ContentSwap activeView={activeView}>
          <div id={MODAL_VIEWS.SEND_PROOF}>
            <StyledModalSubheader>
              {displayContent.strSendTestAsRandomDesc({ isEmail: medium === MessageMedium.Email })}
            </StyledModalSubheader>
            <StyledModalBody>
              <form
                id="form-test-msg-random"
                // method controls what happens when the form is submitted.
                // setting it to dialog is a hack to make sure the autocomplete on the input still works,
                // because the autocomplete will only be populated when the form action completes.
                // see https://iterable.atlassian.net/browse/TNT-5428 for more context
                method="dialog"
                onSubmit={handleSendTestMsg}
              >
                <FormField labelText={displayContent.labelRecipient}>
                  <InputText
                    autoComplete={'on'}
                    data-test="input-test-msg-recipient"
                    id="input-test-msg-recipient"
                    onChange={setRecipient}
                    placeholder={placeholderRecipient}
                    value={recipient}
                    width="100%"
                    autoFocus
                  />
                </FormField>
                <FieldRow>
                  <FormField labelText={displayContent.labelListName}>
                    <BasicFilterSelect
                      data-test="select-test-msg-recipient-list"
                      defaultValues={[displayContent.placeholderSelectList]}
                      id="select-test-msg-recipient-list"
                      inputContent={{ filterPlaceholder: displayContent.placeholderSearchList }}
                      isVirtualized
                      onSelect={([newListId]) => setSelectedListId(parseInt(newListId, 10))}
                      options={memoizedSelectedOptions}
                      onSearchChange={(searchedItem) => setSearchTerm(searchedItem ?? '')}
                      disableInternalFiltering
                      isFullWidth
                    />
                  </FormField>
                  <FormField labelText={displayContent.labelTestUsers}>
                    <InputText
                      autoComplete={'on'}
                      data-lpignore="true"
                      data-test="input-test-msg-test-user-size"
                      id="input-test-msg-test-user-size"
                      onChange={setTestUserSize}
                      size={3}
                      value={testUserSize}
                    />
                  </FormField>
                </FieldRow>
              </form>
            </StyledModalBody>
          </div>
          <div id={MODAL_VIEWS.USER_DATA_SUMMARY}>
            <StyledModalSubheader>
              <TemplateDetailsContainer>
                <TemplateDetailsBlock>
                  {`${displayContent.labelRecipient}: ${recipient}`}
                </TemplateDetailsBlock>
                <TemplateDetailsBlock>
                  {`${displayContent.labelList}: ${
                    selectedListId
                      ? sendProofFilter(selectedListId.toString(), [...emailListOptions])[0].label
                      : ''
                  }`}
                </TemplateDetailsBlock>
                <TemplateDetailsBlock>
                  {`${displayContent.labelTestUsers}: ${testUserSize}`}
                </TemplateDetailsBlock>
              </TemplateDetailsContainer>
            </StyledModalSubheader>
            <ScrollableModalBody>
              <UserDataSummaryTable />
            </ScrollableModalBody>
          </div>
        </ContentSwap>
      )}
      <ModalFooter>
        {activeView === MODAL_VIEWS.SEND_PROOF && (
          <Button
            form="form-test-msg-random"
            data-test="send-test-msg-random-submit"
            isDisabled={disableButton}
            isLoading={isSendingTestMsg}
            size={BUTTON_SIZES.LARGE}
            type="submit"
          >
            {displayContent.btnSend({ isEmail: medium === MessageMedium.Email })}
          </Button>
        )}
      </ModalFooter>
    </StyledModal>
  );
};
